import { type JSX } from 'react'
import { useQuery } from '@/hooks'
import { useRegistry } from '@/hooks/useRegistry'
import { Table } from '@/components/Table'
import type { ColumnDef } from '@tanstack/react-table'
import type { Factbox, FactboxFields } from '@/shared/schemas/factbox'
import { Toolbar } from './Toolbar'
import { useDocuments } from '@/hooks/index/useDocuments'
import { constructQuery } from '@/hooks/index/useDocuments/queries/views/factboxes'
import { fields } from '@/shared/schemas/factbox'
import { Pagination } from '@/components/Table/Pagination'

const PAGE_SIZE = 50

export const FactboxList = ({ columns }: {
  columns: ColumnDef<Factbox, unknown>[]
}): JSX.Element => {
  const [filter] = useQuery()
  const { timeZone } = useRegistry()
  const [{ documentOrigin }] = useQuery(['documentOrigin'])
  const currentPage = typeof filter.page === 'string' ? parseInt(filter.page) : 1

  // Drive the fetch from the QuickFilter radio (synced to URL via TableProvider).
  // Default (no value) shows both sources; selecting one skips the other fetch
  // entirely instead of relying on a client-side row filter.
  const origin = Array.isArray(documentOrigin) ? documentOrigin[0] : documentOrigin
  const withArticleFactboxes: boolean | 'only'
    = origin === 'core/factbox'
      ? false
      : origin === 'core/article'
        ? 'only'
        : true

  const { total } = useDocuments<Factbox, FactboxFields>({
    documentType: 'core/factbox',
    fields,
    query: constructQuery(filter, timeZone),
    sort: [{ field: 'modified', desc: true }],
    size: PAGE_SIZE,
    page: currentPage,
    options: {
      subscribe: true,
      setTableData: true,
      withArticleFactboxes
    }
  })

  return (
    <>
      <Toolbar />
      <Table
        type='Factbox'
        columns={columns}
      />
      {/* Only the standalone-only mode has a hit count that matches the rows on
          screen. The article-side rows are expanded from articles and merged in,
          so in the other two modes the page count stays unknown. */}
      <Pagination
        total={withArticleFactboxes === false ? total : undefined}
        pageSize={PAGE_SIZE}
      />
    </>
  )
}
